/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/ban-types */
const AUTHORIZATION = "Authorization"

export type HttpSettings = {
    credentials?: Credentials
    token?: any
    jsessionid?: string
    includeCredentials?: boolean
}

export type Credentials = { user: string; password: string }
export const BLOB = (response: Response): Promise<Blob> => {
    if (response.ok) {
        return response.blob()
    }
    throw response
}

export const TEXT = (response: Response): Promise<string> => {
    if (response.ok) {
        return response.text()
    }
    throw response
}

export const JSON = <T>(response: Response): Promise<T> => {
    if (response.ok) {
        return response.json()
    }
    throw response
}

/**
 * A wrapper for fetch with support for authentication and preprocessing
 * of responses.
 * @class Http
 * @param authFunc {Function} the authentication function
 * @param authProps {Object} the authentication properties
 */
export class Http {
    private readonly onAuthentication:
        | ((options: RequestInit, authProps: any) => RequestInit)
        | null

    private readonly authenticationProps: { [key: string]: any } | null
    private includeCredentials: boolean
    private onPreprocess: Function | null

    constructor(
        authFunc:
            | ((
                  options: { [key: string]: any },
                  authenticationProps: any
              ) => RequestInit)
            | null,
        authProps: { [key: string]: any } | null
    ) {
        this.onAuthentication = authFunc
        this.authenticationProps = authProps
        this.includeCredentials = false
        this.onPreprocess = null
    }

    /**
     * Http connection with bearer user authentication.
     * @param {String} token the bearer token
     * @param {String} jsessionid optional jsessionid
     * @return {Http}
     */
    static Bearer(token: any, jsessionid?: string): Http {
        return new Http(BearerAuthentication, { token, jsessionid })
    }

    /**
     * Http connection with basic user authentication.
     * @param user {String} the username
     * @param password {String} the user password
     * @return {Http}
     */
    static Basic(user: string, password: string): Http {
        return new Http(BasicAuthentication, { user, password })
    }

    /**
     * Plain http connection with no authentication.
     * @return {Http}
     */
    static Plain(): Http {
        return new Http(null, null)
    }

    /**
     * Plain http connection that splices a jsessionid into the urls when fetching.
     * @param jsessionid the jsessionid
     * @return {Http}
     */
    static JSessionId(jsessionid: string): Http {
        return new Http(null, { jsessionid })
    }

    /**
     * Create a Http instance from settings.
     * @param {Object} settings the settings object
     * @returns {Http}
     * @example { jsessionid: "some-jsessionid"} -> Plain that splices a jsessionid into the urls when fetching.
     * @example { credentials: null} -> Plain
     * @example { credentials: { user: "user", password: "password"}} -> Basic
     * @example { token: "..."} -> Bearer
     */
    static create(settings: HttpSettings): Http {
        settings = settings || {}

        let http

        if (settings.token) {
            http = Http.Bearer(settings.token, settings.jsessionid)
        } else if (settings.jsessionid) {
            http = Http.JSessionId(settings.jsessionid)
        } else if (settings.credentials) {
            http = Http.Basic(
                settings.credentials.user,
                settings.credentials.password
            )
        } else {
            http = Http.Plain()
        }

        return settings.includeCredentials
            ? http.withCredentialsInclude()
            : http
    }

    /**
     * Enables sending credentials - e.g. cookies, authorization headers
     * or TLS client certificates. This is primarily needed for sticky cookies
     * in load balancing scenarios.
     *
     * <p>
     * "Access-Control-Allow-Origin is prohibited from using a wildcard for
     * requests with credentials: 'include'. In such cases, the exact origin
     * must be provided; even if you are using a CORS unblocker extension,
     * the requests will still fail." -
     * {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch#sending_a_request_with_credentials_included Using Fetch - Web APIs | MDN}
     * </p>
     * @return this
     */
    withCredentialsInclude(): Http {
        this.includeCredentials = true
        return this
    }

    /**
     * Set a preprocess function. Will be called with the first fetch result.
     * @param func {Function} the preprocess function
     * @return this
     */
    preprocess(func: (response: Response) => Response): Http {
        this.onPreprocess = func
        return this
    }

    /**
     * Prepare an url for a fetch, adding a jsessionid if one is known.
     * @param url to be prepared
     * @returns {string|*} prepared url
     */
    prepareUrl(url: RequestInfo): string {
        const actualUrl = typeof url === "string" ? url : url.url

        const jsessionid = this.authenticationProps?.jsessionid
        if (jsessionid && !actualUrl.toLowerCase().includes(";jsessionid=")) {
            const u = new URL(actualUrl, location.origin)
            u.pathname += `;jsessionid=${jsessionid}`
            return u.href
        }
        return actualUrl
    }

    /**
     * Wrapper around fetch.
     * @param url {String} the url
     * @param options {Object} the fetch options
     */
    fetch(url: RequestInfo, options?: RequestInit): Promise<Response> {
        return fetch(this.prepareUrl(url), this._authentication(options)).then(
            (r) => this._inspect(r)
        )
    }

    get(url: RequestInfo, options?: RequestInit): Promise<Response> {
        const localOptions = Object.assign({}, options, { method: "GET" })
        return this.fetch(url, localOptions)
    }

    post(url: RequestInfo, options?: RequestInit): Promise<Response> {
        const localOptions = Object.assign({}, options, { method: "POST" })
        return this.fetch(url, localOptions)
    }

    put(url: RequestInfo, options?: RequestInit): Promise<Response> {
        const localOptions = Object.assign({}, options, { method: "PUT" })
        return this.fetch(url, localOptions)
    }

    delete(url: RequestInfo, options?: RequestInit): Promise<Response> {
        const localOptions = Object.assign({}, options, { method: "DELETE" })
        return this.fetch(url, localOptions)
    }

    head(url: RequestInfo, options?: RequestInit): Promise<Response> {
        const localOptions = Object.assign({}, options, { method: "HEAD" })
        return this.fetch(url, localOptions)
    }

    option(url: RequestInfo, options?: RequestInit): Promise<Response> {
        const localOptions = Object.assign({}, options, { method: "OPTION" })
        return this.fetch(url, localOptions)
    }

    /**
     * Download as blob.
     * @param url {String} the url
     * @param options {Object} the fetch options
     * @returns {Blob}
     */
    blob(url: RequestInfo, options?: RequestInit): Promise<Blob> {
        return fetch(this.prepareUrl(url), this._authentication(options)).then(
            BLOB
        )
    }

    /**
     * Download as text.
     * @param url {String} the url
     * @param options {Object} the fetch options
     * @returns {String}
     */
    text(url: RequestInfo, options?: RequestInit): Promise<string> {
        return fetch(this.prepareUrl(url), this._authentication(options)).then(
            TEXT
        )
    }

    /**
     * Download as json.
     * @param url {String} the url
     * @param options {Object} the fetch options
     * @returns {JSON}
     */
    json<T = any>(url: RequestInfo, options?: RequestInit): Promise<T> {
        return fetch(
            this.prepareUrl(url),
            this._authentication(options)
        ).then<T>(JSON)
    }

    /**
     * Internal method to handle authentication.
     * @private
     */
    _authentication(options?: RequestInit): RequestInit | undefined {
        if (this.includeCredentials) {
            // required for sending sticky cookies in load balancing scenarios
            options = { ...options, credentials: "include" }
        } else {
            options = { ...options, credentials: "omit" }
        }

        if (this.onAuthentication) {
            return this.onAuthentication(options, this.authenticationProps)
        }
        return options
    }

    /**
     * Internal method to handle preprocessing.
     * @private
     */
    _inspect(response: Response) {
        if (this.onPreprocess) {
            return this.onPreprocess(response)
        }
        return response
    }
}

/**
 * Handle basic authentication by adding an authentication header.
 * @param fetchParams {Object} the fetch parameters
 * @param args {Object} the authentication arguments, e.g. user and password
 * @return the modified fetch parameters
 * @private
 */
function BasicAuthentication(
    fetchParams: any,
    args: { user: string; password: string }
): { [key: string]: any } {
    fetchParams = fetchParams || {}
    const authorization = "Basic " + btoa(`${args.user}:${args.password}`)

    if (!fetchParams.headers) {
        fetchParams.headers = new Headers()
    }
    fetchParams.headers.append(AUTHORIZATION, authorization)
    return fetchParams
}

/**
 * Handle bearer authentication by adding an authentication header.
 * @param fetchParams {Object} the fetch parameters
 * @param args {Object} the authentication arguments, e.g. user and password
 * @return the modified fetch parameters
 * @private
 */
function BearerAuthentication(
    fetchParams: { [key: string]: any } | null,
    args: { token: any }
): { [key: string]: any } {
    fetchParams = fetchParams || {}
    const token = typeof args.token === "function" ? args.token() : args.token

    if (!fetchParams.headers) {
        fetchParams.headers = new Headers()
    }

    /*
     * An empty or undefined token will be handled as invalid token by some Server API.
     * So do not append it if there is no token (yet). The token could be present later in
     * the application after the user has authenticated successful.
     */
    if (!token) {
        return fetchParams
    }
    const authorization = `Bearer ${token}`
    fetchParams.headers.append(AUTHORIZATION, authorization)
    return fetchParams
}
